import axios from 'axios'

// Admin API service for tenant management
// This service uses the admin API which is on admin.clnicl.ie or staging.admin.clnicl.ie
// It requires Cognito authentication

// Get admin API URL from environment or construct from current domain
function getAdminApiUrl(): string {
  // Check if we have an explicit admin API URL
  const adminApiUrl = import.meta.env.VITE_ADMIN_API_URL
  
  if (adminApiUrl) {
    return adminApiUrl
  }
  
  // Construct from current domain
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    const parts = hostname.split('.')
    
    // If we're on admin.clnicl.ie or staging.admin.clnicl.ie
    if (parts.includes('admin')) {
      // Replace 'admin' with 'api' or use the admin API subdomain
      const domain = parts.slice(-2).join('.') // Get root domain (clnicl.ie)
      const environment = parts[0] === 'admin' ? 'production' : parts[0]
      
      if (environment === 'production') {
        return `https://api.admin.${domain}`
      } else {
        return `https://api.${environment}.admin.${domain}`
      }
    }
  }
  
  // Fallback
  return 'https://api.admin.clnicl.ie'
}

// Create axios instance for admin API
const adminApiClient = axios.create({
  baseURL: getAdminApiUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
adminApiClient.interceptors.request.use(
  (config) => {
    // Get token from Cognito or localStorage
    // In a real implementation, you'd get this from your auth context
    const token = localStorage.getItem('admin_auth_token') || localStorage.getItem('cognito_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
adminApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export interface Tenant {
  subdomain: string
  tenantId: string
  productType?: 'pms' | 'ppt'
  clinicName: string
  contactEmail: string
  contactPersonName?: string
  phoneNumber?: string
  status: 'pending' | 'provisioning' | 'provisioned' | 'failed' | 'deleting' | 'deleted' | 'deletion_failed'
  createdAt: string
  updatedAt: string
  [key: string]: string | number | boolean | undefined // Allow for additional fields
}

export interface TenantUpdateData {
  clinicName?: string
  contactEmail?: string
  contactPersonName?: string
  phoneNumber?: string
  productType?: 'pms' | 'ppt'
}

export const tenantService = {
  /**
   * Get all tenants
   */
  getAll: async (): Promise<Tenant[]> => {
    const response = await adminApiClient.get<{ tenants: Tenant[] }>('/tenants')
    return response.data.tenants
  },

  /**
   * Get a tenant by subdomain
   */
  getBySubdomain: async (subdomain: string): Promise<Tenant> => {
    const response = await adminApiClient.get<{ tenant: Tenant }>(`/tenants/${subdomain}`)
    return response.data.tenant
  },

  /**
   * Update a tenant
   */
  update: async (subdomain: string, data: TenantUpdateData): Promise<Tenant> => {
    const response = await adminApiClient.put<{ tenant: Tenant; message: string }>(
      `/tenants/${subdomain}`,
      data
    )
    return response.data.tenant
  },

  /**
   * Trigger provisioning for a tenant
   */
  triggerProvisioning: async (subdomain: string): Promise<{ message: string; status: string }> => {
    const response = await adminApiClient.post<{ message: string; subdomain: string; status: string }>(
      `/tenants/${subdomain}/provision`
    )
    return { message: response.data.message, status: response.data.status }
  },
}
